import axios, {
  type AxiosError,
  type InternalAxiosRequestConfig,
} from "axios";
import { environment } from "../../configuration";
// Safe despite the circular reference (telemetry/index.ts imports getToken
// from this same file): both sides only use the other's export inside a
// function body (clearTokens() here, buildConfig() there), never at
// module-eval time, so ESM's live-binding resolution settles before either
// is ever called.
import { resetTelemetryIdentity } from "../../lib/telemetry";

const TOKEN_KEY = "token";
const REFRESH_TOKEN_KEY = "refreshToken";

export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);

export const getRefreshToken = (): string | null =>
  localStorage.getItem(REFRESH_TOKEN_KEY);

export const storeTokens = (
  accessToken: string,
  refreshToken?: string
): void => {
  localStorage.setItem(TOKEN_KEY, accessToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
};

export const clearTokens = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  // usage-event-pipeline correction batch 4B item 1: the telemetry TOKEN
  // cache lives in an in-memory module variable (lib/telemetry/token.ts)
  // that survives clearing localStorage — without this, the next person on
  // a shared device would keep getting events attributed to whoever logged
  // out.
  resetTelemetryIdentity();
};

// Non-intercepted instance, used for /auth/login and as the transport for
// /auth/refresh. If the default `api` instance were used for the refresh
// call, a 401 there would recurse back into its own response interceptor.
export const apiLogin = axios.create({
  baseURL: environment.apiUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

export const api = axios.create({
  baseURL: environment.apiUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const AUTH_ENDPOINT_PATTERN = /\/auth\/(refresh|login|google)/i;

const isAuthEndpoint = (url?: string): boolean =>
  !!url && AUTH_ENDPOINT_PATTERN.test(url);

// Clears local session state and sends the user to /login. Uses
// window.location (not react-router) so it also works from a response
// interceptor, outside any React render/effect cycle.
export const hardLogout = (): void => {
  clearTokens();
  window.location.href = "/login";
};

// Single-flight guard: the first 401 in a batch creates this promise, any
// 401 that arrives while it's pending awaits the SAME promise instead of
// firing its own refresh call.
let refreshPromise: Promise<string> | null = null;

const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const { data } = await apiLogin.post("/auth/refresh", { refreshToken });
  storeTokens(data.accessToken, data.refreshToken);
  return data.accessToken;
};

const attachBearerToken = (config: InternalAxiosRequestConfig) => {
  if (isAuthEndpoint(config.url)) {
    return config;
  }

  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
};

const handleResponseError = async (error: AxiosError) => {
  const { response, config } = error;
  const requestConfig = config as RetryableRequestConfig | undefined;

  if (!response || response.status !== 401 || !requestConfig) {
    return Promise.reject(error);
  }

  // /auth/login and /auth/refresh manage their own 401s (invalid
  // credentials / invalid refresh token); never loop them through refresh.
  if (isAuthEndpoint(requestConfig.url)) {
    return Promise.reject(error);
  }

  if (requestConfig._retry) {
    hardLogout();
    return Promise.reject(error);
  }

  requestConfig._retry = true;

  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }

  try {
    const accessToken = await refreshPromise;
    requestConfig.headers.Authorization = `Bearer ${accessToken}`;
    return api(requestConfig);
  } catch {
    hardLogout();
    return Promise.reject(error);
  }
};

// Request side: read the token FRESH on every call (fixes the stale-token
// bug where the Authorization header used to be baked in once at import).
api.interceptors.request.use(attachBearerToken);

// Response side: single-flight 401 -> refresh -> replay.
api.interceptors.response.use((response) => response, handleResponseError);

// Test-only: refreshPromise is module scope, not per-instance, so tests
// need a way to reset it between cases.
export const __resetRefreshStateForTests = (): void => {
  refreshPromise = null;
};
